import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search } from 'lucide-react';
import { getInsights, getSessions, qk } from '../api.js';
import { NAV_ITEMS, useActivePid } from './Layout.js';

/**
 * §76 — ⌘K komut paleti (arama v1).
 *
 * NEDEN VAR: üst çubuktaki arama kutusu M41'e kadar kontrolsüz bir `<input>`tü —
 * `value` yok, `onChange` yok, sonuç paneli yok. Yanındaki "⌘ K" rozeti de yalnız
 * bir `<span>`di; hiçbir kısayol dinleyicisi yoktu. Yani panelin en görünür
 * yerindeki iki öğe hiçbir şey yapmıyordu.
 *
 * §71.2 KARAR 4 — v1 SUNUCUSUZDUR: yeni bir arama ucu, indeks ya da tam-metin
 * motoru açılmaz. Envanterde eksik olan arama ALTYAPISI değil KABUKTU; palet mevcut
 * liste uçlarını (sessions/insights) parametreli çağırır ve gezinme/eylem
 * satırlarını yerel olarak süzer. Sunucu taraflı arama, gerçek talep kanıtlanırsa
 * ayrı bir karardır (§71.4).
 */

/** §76.1 — veri bölümü bu uzunluktan ÖNCE sorgu ATMAZ. */
export const MIN_QUERY_LEN = 2;

/** §76.1 — yazma sırasında her tuşta istek atmamak için bekleme. */
export const DEBOUNCE_MS = 300;

/** §76.1 — her bölümün gösterdiği azami satır (palet bir liste ekranı değildir). */
const SECTION_LIMIT = 5;

/**
 * §76.1 — EYLEM satırları. Hepsi İLGİLİ SAYFAYA GÖTÜRÜR, eylemi paletten
 * TETİKLEMEZ: "Yeniden tara" gibi fiillerin onay diyalogları ve 409 çakışma
 * sözleşmeleri vardır (§24.7) ve paletten tetiklemek onları atlamak olurdu.
 */
const ACTIONS: { label: string; hint: string; to: string }[] = [
  { label: 'Yeni proje', hint: 'Kurulum sihirbazını aç', to: '/kurulum' },
  { label: 'SEO taraması', hint: 'SEO sayfasını aç', to: '/seo' },
  { label: 'Yönetici raporu', hint: 'Uzun Vadeli sayfasını aç', to: '/uzun-vadeli' },
  { label: 'Proje ayarları', hint: 'Ad, alan adı, anahtar, arşiv', to: '/ayarlar' },
  { label: 'Hesabım', hint: 'Parola ve oturumlar', to: '/hesap' },
  { label: 'Webhook’lar', hint: 'Olay abonelikleri', to: '/webhooks' },
];

interface Row {
  key: string;
  section: 'Sayfalar' | 'Eylemler' | 'Veri';
  label: string;
  hint?: string;
  to: string;
}

/** Türkçe duyarlı, aksan/büyük-küçük farkını yok sayan içerik eşleşmesi. */
function normalize(text: string): string {
  return text.toLocaleLowerCase('tr-TR');
}

function matches(haystack: string, needle: string): boolean {
  return normalize(haystack).includes(normalize(needle));
}

export default function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const pid = useActivePid();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [aktif, setAktif] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Palet her açılışta TEMİZ başlar: önceki aramanın sonuçlarıyla açılmak, kullanıcıyı
  // yazmadığı bir sorgunun sonuçlarına bakarken bulurdu.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebounced('');
    setAktif(0);
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const veriAcik = debounced.length >= MIN_QUERY_LEN;

  /*
   * Veri sorguları YALNIZ eşik aşıldığında koşar (`enabled`). Bu bir performans
   * ayarı değil sözleşmedir: iki karakterin altında sunucuya İSTEK GİTMEZ ve DOM
   * testi bunu çağrı SAYISIYLA doğrular (vasıfsız test tuzağı — "sonuç yok" demek
   * sorgunun atılmadığını KANITLAMAZ).
   */
  const sessions = useQuery({
    queryKey: qk.sessions(pid),
    queryFn: ({ signal }) => getSessions(pid, undefined, signal),
    enabled: open && veriAcik,
    retry: false,
  });

  const insights = useQuery({
    queryKey: qk.insights(pid),
    queryFn: ({ signal }) => getInsights(pid, undefined, signal),
    enabled: open && veriAcik,
    retry: false,
  });

  const rows = useMemo<Row[]>(() => {
    const q = debounced;
    const out: Row[] = [];

    const sayfalar = NAV_ITEMS.filter(
      (item) => q === '' || matches(item.label, q) || matches(item.description, q),
    ).slice(0, SECTION_LIMIT);
    for (const item of sayfalar) {
      out.push({
        key: `nav:${item.to}`,
        section: 'Sayfalar',
        label: item.label,
        hint: item.description,
        to: item.to,
      });
    }

    const eylemler = ACTIONS.filter(
      (a) => q === '' || matches(a.label, q) || matches(a.hint, q),
    ).slice(0, SECTION_LIMIT);
    for (const a of eylemler) {
      out.push({ key: `act:${a.to}:${a.label}`, section: 'Eylemler', label: a.label, hint: a.hint, to: a.to });
    }

    if (veriAcik) {
      /*
       * Uçlar §33.5 liste ZARFI döner (`{items, limit, truncated}`); palet zarfın
       * `items`ini okur ve `truncated` bilgisini alt satırdaki tavan cümlesiyle
       * kullanıcıya söyler.
       */
      const bulgular = (insights.data?.items ?? [])
        .filter((i) => matches(i.title, q) || matches(i.rule_id, q))
        .slice(0, SECTION_LIMIT);
      for (const i of bulgular) {
        out.push({
          key: `ins:${i.id}`,
          section: 'Veri',
          label: i.title,
          hint: `Bulgu · ${i.rule_id}`,
          to: '/bulgular',
        });
      }

      const oturumlar = (sessions.data?.items ?? [])
        .filter((s) => matches(s.session_id, q) || matches(s.aid, q))
        .slice(0, SECTION_LIMIT);
      for (const s of oturumlar) {
        out.push({
          key: `ses:${s.session_id}`,
          section: 'Veri',
          label: s.session_id,
          hint: `Oturum · ${s.device} · ${s.channel}`,
          /*
           * `?sid=` — Oturumlar sayfasının KENDİ derin bağlantı sözleşmesi (Bulgular
           * sayfasındaki "örnek oturumlar" linkleri de bunu kullanır). Palet ikinci
           * bir seçim mekanizması icat etmez.
           */
          to: `/oturumlar?sid=${encodeURIComponent(s.session_id)}`,
        });
      }
    }

    return out;
  }, [debounced, veriAcik, insights.data, sessions.data]);

  // Seçim listeyle uyumlu tutulur: sonuçlar daralınca imleç listenin dışında kalmasın.
  useEffect(() => {
    setAktif((mevcut) => (mevcut >= rows.length ? 0 : mevcut));
  }, [rows.length]);

  if (!open) return null;

  function sec(row: Row | undefined): void {
    if (row === undefined) return;
    onClose();
    navigate(row.to);
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/20 px-4 pt-[12vh]"
      onMouseDown={(event) => {
        // Yalnız ZEMİNE tıklama kapatır; panelin içindeki tıklama geçmez.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Komut paleti"
        data-testid="komut-paleti"
        className="w-full max-w-[560px] overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-[#f3f4f6] px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-[#9ca3af]" aria-hidden="true" />
          <input
            ref={inputRef}
            data-testid="palet-girdi"
            type="text"
            value={query}
            placeholder="Sayfa, oturum veya bulgu ara"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setAktif((i) => (rows.length === 0 ? 0 : (i + 1) % rows.length));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setAktif((i) => (rows.length === 0 ? 0 : (i - 1 + rows.length) % rows.length));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                sec(rows[aktif]);
              } else if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
              }
            }}
            className="flex-1 bg-transparent text-[14px] text-[#111827] outline-none placeholder:text-[#9ca3af]"
          />
          <kbd className="shrink-0 rounded border border-[#e5e7eb] bg-[#f9fafb] px-1.5 py-0.5 text-[10px] text-[#9ca3af]">
            esc
          </kbd>
        </div>

        <div className="max-h-[50vh] overflow-y-auto py-1">
          {rows.length === 0 ? (
            <p data-testid="palet-sonuc-yok" className="px-4 py-6 text-center text-[13px] text-[#9ca3af]">
              Sonuç yok.
            </p>
          ) : (
            rows.map((row, index) => {
              const oncekiBolum = index === 0 ? null : rows[index - 1]?.section;
              return (
                <div key={row.key}>
                  {row.section !== oncekiBolum ? (
                    <p className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]">
                      {row.section}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    data-testid="palet-satir"
                    data-section={row.section}
                    aria-selected={index === aktif}
                    onMouseEnter={() => setAktif(index)}
                    onClick={() => sec(row)}
                    className={`flex w-full items-center justify-between gap-3 px-4 py-2 text-left transition-colors ${
                      index === aktif ? 'bg-[#fff4ec]' : 'hover:bg-[#f9fafb]'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium text-[#111827]">
                        {row.label}
                      </span>
                      {row.hint !== undefined ? (
                        <span className="block truncate text-[12px] text-[#9ca3af]">{row.hint}</span>
                      ) : null}
                    </span>
                    {index === aktif ? (
                      <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden="true" />
                    ) : null}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/*
          §76.2 — TAVAN SÖYLENİR. Veri bölümü mevcut liste uçlarının döndüğü ilk N
          satır içinde arar; "aradığım oturum çıkmadı" diyen kullanıcıya sessiz
          kalmak, aramanın bozuk olduğunu düşündürürdü (§33.5 `truncated` sözleşmesinin
          palet karşılığı).
        */}
        <p className="border-t border-[#f3f4f6] px-4 py-2 text-[11px] text-[#9ca3af]">
          {veriAcik
            ? 'Veri araması yüklenmiş son kayıtlar içinde yapılır; her bölümde ilk 5 sonuç gösterilir.'
            : `Oturum ve bulgu aramak için en az ${MIN_QUERY_LEN} karakter yazın.`}
        </p>
      </div>
    </div>
  );
}
